import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, addDoc, collection, updateDoc } from "firebase/firestore";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  FormGroup,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

const EntryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState({
    Author: {
      name: "",
      pseudonym: "",
      initials: "",
      gender_attribution: "",
      attribution: "",
      source_of_attribution: "",
    },
    Title: {
      short_version: "",
      full_title: "",
      title_subtitle_keyword: {
        romance: false,
        history: false,
        novel: false,
        story: false,
        memoir: false,
        letters: false,
        adventures: false,
        tale_s: false,
        journal: false,
        other: "",
      },
    },
    Source: {
      main_source_1: { author: "", title: "", language: "", genre: "" },
      main_source_2: { author: "", title: "", language: "", genre: "" },
      main_source_3: { author: "", title: "", language: "", genre: "" },
    },
    Translation: {
      is_translation: false,
      actual_translation: false,
      translator_name: "",
      translator_gender: "",
      original_title: "",
      original_language: "",
      includes_verse: false,
    },
    Bibliographical_details: {
      year_of_first_edition: "",
      other_editions: "",
      place_of_publication: "",
      bookseller_s: "",
      printer: "",
      format: "",
      citation_number: "",
      pdf_url: "",
    },
    Dedicatee_s: [
      { name: "", gender: "" },
      { name: "", gender: "" },
      { name: "", gender: "" },
      { name: "", gender: "" },
      { name: "", gender: "" },
      { name: "", gender: "" },
    ],
    Genre_subgenre: {
      romance: false,
      novella_short_fiction: false,
      religious_moral_allegory: false,
      didactic_fiction: false,
      jest_book: false,
      picaresque_and_roguery: false,
      travel_literature: false,
      popular_miscellany: false,
      memoir: false,
      scandal_chronicle_secret_history: false,
      historical_novel: false,
      novel_of_gallantry: false,
      political_allegorical_novel: false,
      oriental_tale: false,
      fairy_tale: false,
      epistolary_form: false,
      fictional_essay: false,
      other: "",
    },
    Paratexts: {
      is_paratext: false,
      frontispiece: false,
      images: false,
      intertitle: false,
      dedicatory_epistle: false,
      preface_to_the_reader: false,
      introduction: false,
      poems: false,
      annotations: false,
      contents_page: false,
      index: false,
      errata_sheets: false,
      advertisement_or_catalogue: false,
      other: "",
    },
    Setting: {
      time: {
        contemporary: false,
        pre_war_period: false,
        civil_war_and_interregnum: false,
        restoration: false,
        unidentified: false,
        historical_past_unspecified: false,
        ancient_time: "",
        middle_ages: false,
        renaissance: false,
        future: false,
        unspecified: false,
      },
      place: {
        england: false,
        europe: "",
        the_americas: "",
        africa: "",
        asia: "",
        other_worlds_imaginary: "",
        the_moon: false,
      },
      social_loci: {
        country: false,
        city: false,
        village: false,
        court: false,
        the_wild: false,
      },
      location_topoi: {
        garden: false,
        grotto_cave: false,
        sea: false,
        heaven: false,
        church: false,
        convent: false,
        bedroom: false,
        chamber: false,
        prison: false,
        castle: false,
        inn: false,
        other: "",
      },
    },
    Summary: {
      keywords: {
        courtship: false,
        elopement: false,
        marriage: false,
        murder: false,
        illness: false,
        suicide: false,
        parricide: false,
        other: "",
      },
      short_summary: "",
    },
  });

  useEffect(() => {
    const fetchEntry = async () => {
      if (id) {
        const docRef = doc(db, "entries", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();

          // Migrar Dedicatee_s del formato antiguo al nuevo si es necesario
          if (data.Dedicatee_s && !Array.isArray(data.Dedicatee_s)) {
            // Formato antiguo: { dedicatee_1: "name", dedicatee_2: "name", ... }
            // Convertir a formato nuevo: [{ name: "name", gender: "" }, ...]
            const oldDedicatees = data.Dedicatee_s;
            data.Dedicatee_s = [
              { name: oldDedicatees.dedicatee_1 || "", gender: "" },
              { name: oldDedicatees.dedicatee_2 || "", gender: "" },
              { name: oldDedicatees.dedicatee_3 || "", gender: "" },
              { name: oldDedicatees.dedicatee_4 || "", gender: "" },
              { name: oldDedicatees.dedicatee_5 || "", gender: "" },
              { name: oldDedicatees.dedicatee_6 || "", gender: "" },
            ];
          }

          setEntry(data);
        }
      }
    };

    fetchEntry();
  }, [id]);

  const placeholderRegex =
    /(lorem ipsum|\blorem\b|dolor sit amet|consectetur adipiscing|sed do eiusmod|tempor incididunt|ut labore et dolore)/i;

  const hasPlaceholderContent = (value) => {
    if (typeof value === "string") {
      return placeholderRegex.test(value);
    }

    if (Array.isArray(value)) {
      return value.some((item) => hasPlaceholderContent(item));
    }

    if (value && typeof value === "object") {
      return Object.values(value).some((item) => hasPlaceholderContent(item));
    }

    return false;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const keys = name.split(".");
    const newValue = type === "checkbox" ? checked : value;

    setEntry((prevEntry) => {
      const newEntry = { ...prevEntry };
      let current = newEntry;

      // Navigate to the nested property
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const isArrayIndex = !isNaN(parseInt(key));

        if (isArrayIndex) {
          // Handle array access
          const index = parseInt(key);
          if (!Array.isArray(current)) {
            current = [];
          }
          if (!current[index]) {
            current[index] = {};
          } else {
            current[index] = { ...current[index] };
          }
          current = current[index];
        } else {
          // Handle object access
          if (!current[key]) {
            current[key] = {};
          } else if (Array.isArray(current[key])) {
            current[key] = [...current[key]];
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
      }

      // Set the final value
      const finalKey = keys[keys.length - 1];
      current[finalKey] = newValue;

      return newEntry;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (hasPlaceholderContent(entry)) {
      alert(
        "This entry contains placeholder text (e.g., lorem ipsum). Please review and remove test content before saving.",
      );
      return;
    }

    if (id) {
      await updateDoc(doc(db, "entries", id), entry);
    } else {
      await addDoc(collection(db, "entries"), entry);
    }
    navigate("/");
  };

  const settingLabels = {
    pre_war_period: "Pre-war",
    civil_war_and_interregnum: "Civil War and Interregnum",
    restoration: "Restoration",
    middle_ages: "Middle Ages",
    england: "England",
    grotto_cave: "grotto / cave",
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1400, margin: "0 auto" }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ color: "#1976d2", fontWeight: "bold", mb: 3 }}
        >
          {id ? "Edit Entry" : "New Entry"}
        </Typography>

        <form onSubmit={handleSubmit}>
          {/* AUTHOR SECTION */}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#e3f2fd" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Author Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="Author.name"
                    value={entry.Author.name}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Pseudonym"
                    name="Author.pseudonym"
                    value={entry.Author.pseudonym}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Initials"
                    name="Author.initials"
                    value={entry.Author.initials}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Gender Attribution"
                    name="Author.gender_attribution"
                    value={entry.Author.gender_attribution}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Attribution"
                    name="Author.attribution"
                    value={entry.Author.attribution}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Source of Attribution"
                    name="Author.source_of_attribution"
                    value={entry.Author.source_of_attribution}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TITLE SECTION */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#e8f5e9" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Title Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Short Version"
                    name="Title.short_version"
                    value={entry.Title.short_version}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Title"
                    name="Title.full_title"
                    value={entry.Title.full_title}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Title/Subtitle Keywords
                  </Typography>
                  <FormGroup row>
                    {[
                      "romance",
                      "history",
                      "novel",
                      "story",
                      "memoir",
                      "letters",
                      "adventures",
                      "tale_s",
                      "journal",
                    ].map((keyword) => (
                      <FormControlLabel
                        key={keyword}
                        control={
                          <Checkbox
                            name={`Title.title_subtitle_keyword.${keyword}`}
                            checked={
                              entry.Title.title_subtitle_keyword[keyword]
                            }
                            onChange={handleChange}
                            size="small"
                          />
                        }
                        label={
                          keyword.charAt(0).toUpperCase() + keyword.slice(1)
                        }
                      />
                    ))}
                  </FormGroup>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Other Keywords"
                    name="Title.title_subtitle_keyword.other"
                    value={entry.Title.title_subtitle_keyword.other}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SOURCE SECTION */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#fff3e0" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Source Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {["main_source_1", "main_source_2", "main_source_3"].map(
                (source, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 2 }}
                    >
                      Main Source {index + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Author"
                          name={`Source.${source}.author`}
                          value={entry.Source[source].author}
                          onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Title"
                          name={`Source.${source}.title`}
                          value={entry.Source[source].title}
                          onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Language"
                          name={`Source.${source}.language`}
                          value={entry.Source[source].language}
                          onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Genre"
                          name={`Source.${source}.genre`}
                          value={entry.Source[source].genre}
                          onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ),
              )}
            </AccordionDetails>
          </Accordion>

          {/* TRANSLATION SECTION */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#f3e5f5" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Translation Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="Translation.actual_translation"
                        checked={entry.Translation.actual_translation}
                        onChange={handleChange}
                      />
                    }
                    label="Translation"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Translator's name"
                    name="Translation.translator_name"
                    value={entry.Translation.translator_name}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Translator's gender"
                    name="Translation.translator_gender"
                    value={entry.Translation.translator_gender}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Original Title"
                    name="Translation.original_title"
                    value={entry.Translation.original_title}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Original Language"
                    name="Translation.original_language"
                    value={entry.Translation.original_language}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="Translation.includes_verse"
                        checked={entry.Translation.includes_verse}
                        onChange={handleChange}
                      />
                    }
                    label="Includes Verse?"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* BIBLIOGRAPHICAL DETAILS */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#fce4ec" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Bibliographical Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Year of First Edition"
                    name="Bibliographical_details.year_of_first_edition"
                    value={entry.Bibliographical_details.year_of_first_edition}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Place of Publication"
                    name="Bibliographical_details.place_of_publication"
                    value={entry.Bibliographical_details.place_of_publication}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Format"
                    name="Bibliographical_details.format"
                    value={entry.Bibliographical_details.format}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Printer"
                    name="Bibliographical_details.printer"
                    value={entry.Bibliographical_details.printer}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Bookseller"
                    name="Bibliographical_details.bookseller_s"
                    value={entry.Bibliographical_details.bookseller_s}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Citation Number"
                    name="Bibliographical_details.citation_number"
                    value={entry.Bibliographical_details.citation_number}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Other Editions"
                    name="Bibliographical_details.other_editions"
                    value={entry.Bibliographical_details.other_editions}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="PDF URL"
                    name="Bibliographical_details.pdf_url"
                    value={entry.Bibliographical_details.pdf_url}
                    onChange={handleChange}
                    size="small"
                    placeholder="https://..."
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* DEDICATEES */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#e0f2f1" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Dedicatees
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Paper
                      elevation={1}
                      sx={{ p: 2, backgroundColor: "#f9f9f9" }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        Dedicatee {index + 1}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            label="Name"
                            name={`Dedicatee_s.${index}.name`}
                            value={entry.Dedicatee_s[index]?.name || ""}
                            onChange={handleChange}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Gender"
                            name={`Dedicatee_s.${index}.gender`}
                            value={entry.Dedicatee_s[index]?.gender || ""}
                            onChange={handleChange}
                            size="small"
                            placeholder="M/F"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* GENRE/SUBGENRE */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#ede7f6" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Genre / Subgenre
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup row>
                {[
                  "romance",
                  "novella_short_fiction",
                  "religious_moral_allegory",
                  "didactic_fiction",
                  "jest_book",
                  "picaresque_and_roguery",
                  "travel_literature",
                  "popular_miscellany",
                  "memoir",
                  "scandal_chronicle_secret_history",
                  "historical_novel",
                  "novel_of_gallantry",
                  "political_allegorical_novel",
                  "oriental_tale",
                  "fairy_tale",
                  "epistolary_form",
                  "fictional_essay",
                ].map((genre) => (
                  <Grid item xs={12} sm={6} md={4} key={genre}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={`Genre_subgenre.${genre}`}
                          checked={entry.Genre_subgenre[genre]}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label={genre.replace(/_/g, " ")}
                    />
                  </Grid>
                ))}
              </FormGroup>
              <TextField
                fullWidth
                label="Other Genres"
                name="Genre_subgenre.other"
                value={entry.Genre_subgenre.other}
                onChange={handleChange}
                size="small"
                sx={{ mt: 2 }}
              />
            </AccordionDetails>
          </Accordion>

          {/* PARATEXTS */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#fff9c4" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Paratexts
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup row>
                {[
                  "frontispiece",
                  "images",
                  "intertitle",
                  "dedicatory_epistle",
                  "preface_to_the_reader",
                  "introduction",
                  "poems",
                  "annotations",
                  "contents_page",
                  "index",
                  "errata_sheets",
                  "advertisement_or_catalogue",
                ].map((paratext) => (
                  <Grid item xs={12} sm={6} md={4} key={paratext}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={`Paratexts.${paratext}`}
                          checked={entry.Paratexts[paratext]}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label={paratext.replace(/_/g, " ")}
                    />
                  </Grid>
                ))}
              </FormGroup>
              <TextField
                fullWidth
                label="Other Paratexts"
                name="Paratexts.other"
                value={entry.Paratexts.other}
                onChange={handleChange}
                size="small"
                sx={{ mt: 2 }}
              />
            </AccordionDetails>
          </Accordion>

          {/* SETTING */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#e1f5fe" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Setting
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Time
              </Typography>
              <FormGroup row>
                {[
                  "contemporary",
                  "pre_war_period",
                  "civil_war_and_interregnum",
                  "restoration",
                  "unidentified",
                  "historical_past_unspecified",
                  "middle_ages",
                  "renaissance",
                  "future",
                  "unspecified",
                ].map((time) => (
                  <Grid item xs={12} sm={6} md={4} key={time}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={`Setting.time.${time}`}
                          checked={entry.Setting.time[time]}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label={settingLabels[time] ?? time.replace(/_/g, " ")}
                    />
                  </Grid>
                ))}
              </FormGroup>
              <TextField
                fullWidth
                label="Ancient Time (specify)"
                name="Setting.time.ancient_time"
                value={entry.Setting.time.ancient_time}
                onChange={handleChange}
                size="small"
                sx={{ mt: 2, mb: 3 }}
              />

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Place
              </Typography>
              <FormGroup row>
                {["england", "the_moon"].map((place) => (
                  <Grid item xs={12} sm={6} md={4} key={place}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={`Setting.place.${place}`}
                          checked={entry.Setting.place[place]}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label={settingLabels[place] ?? place.replace(/_/g, " ")}
                    />
                  </Grid>
                ))}
              </FormGroup>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Europe (specify)"
                    name="Setting.place.europe"
                    value={entry.Setting.place.europe}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="The Americas (specify)"
                    name="Setting.place.the_americas"
                    value={entry.Setting.place.the_americas}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Africa (specify)"
                    name="Setting.place.africa"
                    value={entry.Setting.place.africa}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Asia (specify)"
                    name="Setting.place.asia"
                    value={entry.Setting.place.asia}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Other Worlds / Imaginary"
                    name="Setting.place.other_worlds_imaginary"
                    value={entry.Setting.place.other_worlds_imaginary}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
              </Grid>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mt: 3, mb: 1 }}
              >
                Social Loci
              </Typography>
              <FormGroup row>
                {["country", "city", "village", "court", "the_wild"].map(
                  (loci) => (
                    <Grid item xs={12} sm={6} md={4} key={loci}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name={`Setting.social_loci.${loci}`}
                            checked={entry.Setting.social_loci[loci]}
                            onChange={handleChange}
                            size="small"
                          />
                        }
                        label={loci.replace(/_/g, " ")}
                      />
                    </Grid>
                  ),
                )}
              </FormGroup>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mt: 3, mb: 1 }}
              >
                Location Topoi
              </Typography>
              <FormGroup row>
                {[
                  "garden",
                  "grotto_cave",
                  "sea",
                  "heaven",
                  "church",
                  "convent",
                  "bedroom",
                  "chamber",
                  "prison",
                  "castle",
                  "inn",
                ].map((topoi) => (
                  <Grid item xs={12} sm={6} md={4} key={topoi}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={`Setting.location_topoi.${topoi}`}
                          checked={entry.Setting.location_topoi[topoi]}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label={settingLabels[topoi] ?? topoi.replace(/_/g, " ")}
                    />
                  </Grid>
                ))}
              </FormGroup>
              <TextField
                fullWidth
                label="Other Location Topoi"
                name="Setting.location_topoi.other"
                value={entry.Setting.location_topoi.other}
                onChange={handleChange}
                size="small"
                sx={{ mt: 2 }}
              />
            </AccordionDetails>
          </Accordion>

          {/* SUMMARY */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#f1f8e9" }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Summary
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" gutterBottom>
                Keywords
              </Typography>
              <FormGroup row>
                {[
                  "courtship",
                  "elopement",
                  "marriage",
                  "murder",
                  "illness",
                  "suicide",
                  "parricide",
                ].map((keyword) => (
                  <Grid item xs={12} sm={6} md={4} key={keyword}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={`Summary.keywords.${keyword}`}
                          checked={entry.Summary.keywords[keyword]}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label={keyword.replace(/_/g, " ")}
                    />
                  </Grid>
                ))}
              </FormGroup>
              <TextField
                fullWidth
                label="Other Keywords"
                name="Summary.keywords.other"
                value={entry.Summary.keywords.other}
                onChange={handleChange}
                size="small"
                sx={{ mt: 2, mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Short Summary"
                name="Summary.short_summary"
                value={entry.Summary.short_summary}
                onChange={handleChange}
              />
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 4 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={() => navigate("/")}
              size="large"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              startIcon={<SaveIcon />}
              size="large"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Save Entry
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default EntryForm;
